import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { getApps, initializeApp } from "firebase/app";
import {
  ANDROID_CHANNEL_ID,
  SERVICE_WORKER_SOURCE,
  SERVICE_WORKER_URL,
  isWebPushConfigured,
  webFirebaseConfig,
  webVapidKey,
} from "./config";

const NATIVE_PLATFORMS = { android: "ANDROID", ios: "IOS" };
const platform = NATIVE_PLATFORMS[Capacitor.getPlatform()] ?? "WEB";
const isNative = platform !== "WEB";

/** The plugin reports iOS/Android "prompt-with-rationale"; callers only care about three states. */
const toPermission = (receive) =>
  receive === "granted" || receive === "denied" ? receive : "prompt";

/** Browsers: the service worker hands pushes and taps to the open tab as messages of this shape. */
const onServiceWorkerMessage = (type, listener) => {
  const onMessage = (event) => {
    if (event.data?.source === SERVICE_WORKER_SOURCE && event.data.type === type)
      listener(event.data);
  };
  navigator.serviceWorker?.addEventListener("message", onMessage);
  return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
};

/**
 * The plugin's web implementation reads the default Firebase app as soon as it is first used, so the
 * app has to exist before any call reaches it; initialising lazily inside obtainToken is too late.
 */
if (!isNative && isWebPushConfigured() && getApps().length === 0) {
  initializeApp(webFirebaseConfig);
}

let serviceWorker;
/** Web only: the service worker FCM delivers background pushes to. */
const prepareWeb = () => {
  serviceWorker ??= (async () => {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL);
    await navigator.serviceWorker.ready;
    return registration;
  })();
  return serviceWorker;
};

const webIsSupported = async () =>
  isWebPushConfigured() && (await FirebaseMessaging.isSupported()).isSupported;

const ensureAndroidChannel = () =>
  platform === "ANDROID"
    ? FirebaseMessaging.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: "Notifications",
        importance: 4,
      })
    : Promise.resolve();

/** Push transport over FCM, with one code path for Android, iOS and browsers. */
export const fcmClient = {
  platform,

  isSupported: () => (isNative ? Promise.resolve(true) : webIsSupported()),

  getPermission: async () => toPermission((await FirebaseMessaging.checkPermissions()).receive),

  requestPermission: async () =>
    toPermission((await FirebaseMessaging.requestPermissions()).receive),

  obtainToken: async () => {
    if (isNative) {
      await ensureAndroidChannel();
      return (await FirebaseMessaging.getToken()).token;
    }
    const serviceWorkerRegistration = await prepareWeb();
    return (await FirebaseMessaging.getToken({ vapidKey: webVapidKey, serviceWorkerRegistration }))
      .token;
  },

  deleteToken: () => FirebaseMessaging.deleteToken(),

  /** A push arrived while the app is open. Resolves to a function that stops listening. */
  onForegroundMessage: async (listener) => {
    if (isNative) {
      const handle = await FirebaseMessaging.addListener(
        "notificationReceived",
        ({ notification }) =>
          listener({
            title: notification?.title,
            body: notification?.body,
            data: notification?.data ?? {},
          })
      );
      return () => handle.remove();
    }
    return onServiceWorkerMessage("notification-received", ({ title, body, data }) =>
      listener({ title, body, data: data ?? {} })
    );
  },

  /** The user tapped a push. Native reports it through the plugin, browsers through the service worker. */
  onNotificationOpened: async (listener) => {
    if (isNative) {
      const handle = await FirebaseMessaging.addListener(
        "notificationActionPerformed",
        ({ notification }) => listener(notification?.data ?? {})
      );
      return () => handle.remove();
    }
    return onServiceWorkerMessage("notification-click", ({ data }) => listener(data ?? {}));
  },
};
